/**
 * Team management — invite members, change roles, remove.
 *
 * Permissions enforced server-side; UI hides controls the current
 * user can't use:
 *   - OWNER: can do everything
 *   - ADMIN: can invite + remove MEMBER/VIEWER, can't touch admins
 *            or owner, can't change own role
 *   - MEMBER/VIEWER: read-only view of the team
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Users,
  UserPlus,
  Crown,
  Shield,
  User as UserIcon,
  Eye,
  Loader2,
  X,
  Mail,
  Trash2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import DashboardLayout from '../components/DashboardLayout';
import teamService, {
  TeamView, MemberOut, InviteOut, Role,
} from '../services/teamService';


const ROLE_OPTIONS: { value: Role; label: string; description: string }[] = [
  { value: 'admin', label: 'Admin', description: 'Everything except billing + ownership transfer' },
  { value: 'member', label: 'Member', description: 'CRUD matches/investors/companies, send outreach' },
  { value: 'viewer', label: 'Viewer', description: 'Read-only. No outreach, no exports' },
];


const roleIcon = (role: Role) => {
  switch (role) {
    case 'owner': return <Crown className="w-3.5 h-3.5" />;
    case 'admin': return <Shield className="w-3.5 h-3.5" />;
    case 'member': return <UserIcon className="w-3.5 h-3.5" />;
    case 'viewer': return <Eye className="w-3.5 h-3.5" />;
  }
};


const roleStyle = (role: Role): string => {
  switch (role) {
    case 'owner': return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'admin': return 'bg-indigo-50 text-indigo-700 border-indigo-200';
    case 'member': return 'bg-slate-50 text-slate-700 border-slate-200';
    case 'viewer': return 'bg-gray-50 text-gray-600 border-gray-200';
  }
};


const RoleBadge: React.FC<{ role: Role }> = ({ role }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium border rounded-md ${roleStyle(role)}`}>
    {roleIcon(role)}
    {role.charAt(0).toUpperCase() + role.slice(1)}
  </span>
);


const TeamPage: React.FC = () => {
  const [view, setView] = useState<TeamView | null>(null);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<Role>('member');
  const [inviteMessage, setInviteMessage] = useState('');
  const [inviteSending, setInviteSending] = useState(false);
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  const load = async () => {
    setLoading(true);
    const res = await teamService.getTeam();
    setLoading(false);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    setView(res.data || null);
  };

  useEffect(() => { load(); }, []);

  const canManage = view?.my_role === 'owner' || view?.my_role === 'admin';
  const isOwner = view?.my_role === 'owner';

  const availableInviteRoles = useMemo(() => {
    // Only OWNER can invite ADMINs (server enforces; mirror in UI).
    if (isOwner) return ROLE_OPTIONS;
    return ROLE_OPTIONS.filter((r) => r.value !== 'admin');
  }, [isOwner]);

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) {
      toast.error('Email required');
      return;
    }
    setInviteSending(true);
    const res = await teamService.inviteMember(
      inviteEmail.trim(), inviteRole, inviteMessage.trim() || undefined,
    );
    setInviteSending(false);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    toast.success(`Invite sent to ${inviteEmail.trim()}`);
    setInviteEmail('');
    setInviteMessage('');
    setShowInvite(false);
    await load();
  };

  const handleRoleChange = async (m: MemberOut, role: Role) => {
    if (m.role === role) return;
    setUpdatingId(m.user_id);
    const res = await teamService.updateMemberRole(m.user_id, role);
    setUpdatingId(null);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    toast.success(`${m.email} → ${role}`);
    await load();
  };

  const handleRemove = async (m: MemberOut) => {
    if (!window.confirm(`Remove ${m.email} from the team?`)) return;
    setUpdatingId(m.user_id);
    const res = await teamService.removeMember(m.user_id);
    setUpdatingId(null);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    toast.success(`${m.email} removed`);
    await load();
  };

  const handleRevokeInvite = async (inv: InviteOut) => {
    if (!window.confirm(`Revoke pending invite to ${inv.email}?`)) return;
    const res = await teamService.revokeInvite(inv.id);
    if (res.error) {
      toast.error(res.error);
      return;
    }
    toast.success(`Invite to ${inv.email} revoked`);
    await load();
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-semibold flex items-center gap-2">
              <Users className="w-7 h-7 text-indigo-500" />
              Team
            </h1>
            {view && (
              <p className="text-gray-600 mt-1">
                {view.organization.name} · you are{' '}
                <RoleBadge role={view.my_role} />
              </p>
            )}
          </div>
          {canManage && !showInvite && (
            <button
              onClick={() => setShowInvite(true)}
              className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
            >
              <UserPlus className="w-4 h-4" />
              Invite member
            </button>
          )}
        </div>

        {showInvite && (
          <form
            onSubmit={handleInvite}
            className="bg-white border border-gray-200 rounded-lg p-5 space-y-3"
          >
            <div className="flex items-center justify-between">
              <h2 className="font-medium">Send an invitation</h2>
              <button
                type="button"
                onClick={() => setShowInvite(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="teammate@example.com"
                  required
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Role</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as Role)}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  {availableInviteRoles.map((r) => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="text-xs text-gray-500">
              {ROLE_OPTIONS.find((r) => r.value === inviteRole)?.description}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Personal note (optional)
              </label>
              <textarea
                value={inviteMessage}
                onChange={(e) => setInviteMessage(e.target.value)}
                placeholder="Hey, wanted to bring you on for the deal flow piece…"
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                rows={2}
              />
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowInvite(false)}
                className="px-3 py-2 text-sm font-medium rounded-md text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={inviteSending}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:bg-gray-300"
              >
                {inviteSending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Send invite
              </button>
            </div>
          </form>
        )}

        {/* Members */}
        <section>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Members ({view?.members.length || 0})
          </h2>
          {loading ? (
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-200">
              {view?.members.map((m) => {
                const isUpdating = updatingId === m.user_id;
                // Server enforces: cannot change own role; cannot demote owner;
                // only OWNER can change ADMIN role. Mirror UI:
                const canTouch = canManage && !m.is_self && !m.is_owner;
                const canSetAdmin = isOwner;
                const allowedRoles = canSetAdmin
                  ? (['admin', 'member', 'viewer'] as Role[])
                  : (['member', 'viewer'] as Role[]);

                return (
                  <div key={m.user_id} className="flex items-center justify-between p-4">
                    <div className="min-w-0">
                      <div className="font-medium truncate">
                        {m.full_name || m.email}
                        {m.is_self && <span className="ml-2 text-xs text-gray-400">(you)</span>}
                      </div>
                      {m.full_name && (
                        <div className="text-sm text-gray-500 truncate">{m.email}</div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {canTouch ? (
                        <select
                          value={m.role}
                          disabled={isUpdating}
                          onChange={(e) => handleRoleChange(m, e.target.value as Role)}
                          className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        >
                          {allowedRoles.map((r) => (
                            <option key={r} value={r}>{r}</option>
                          ))}
                        </select>
                      ) : (
                        <RoleBadge role={m.role} />
                      )}
                      {canTouch && (
                        <button
                          onClick={() => handleRemove(m)}
                          disabled={isUpdating}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                          title={`Remove ${m.email}`}
                        >
                          {isUpdating ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Pending invites */}
        {!loading && view && view.pending_invites.length > 0 && (
          <section>
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Pending invites
            </h2>
            <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-200">
              {view.pending_invites.map((inv) => (
                <div key={inv.id} className="flex items-center justify-between p-4">
                  <div className="min-w-0 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-gray-400" />
                    <div className="min-w-0">
                      <div className="font-medium truncate">{inv.email}</div>
                      <div className="text-xs text-gray-500">
                        Invited as {inv.role} · expires {new Date(inv.expires_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                  {canManage && (
                    <button
                      onClick={() => handleRevokeInvite(inv)}
                      className="text-xs px-2 py-1 text-red-600 hover:bg-red-50 rounded"
                    >
                      Revoke
                    </button>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    </DashboardLayout>
  );
};

export default TeamPage;
